import React, { useState } from 'react';
import axios from 'axios';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Heading from '~/components/core/TextComponents/Heading';
import PlusIcon from '~/components/icons/PlusIcon';
import OverrideRulesTable from '~/components/SystemConfiguration/DelegatedAuthority/OverrideRules/OverrideRulesTable';
import UpsertOverrideRuleDialog from '~/components/SystemConfiguration/DelegatedAuthority/OverrideRules/UpsertOverrideRuleDialog';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  AuthorityLevelInfo,
  EnableOverrideRuleParams,
  OverrideRuleWithOverridesInfo,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface OverrideRulesSectionProps {
  organization: SysconfigOrganization;
  organizationId: number;
  arePaymentLimitsSplit: boolean;
}

const OverrideRulesSection: React.FC<OverrideRulesSectionProps> = ({
  organization,
  organizationId,
  arePaymentLimitsSplit,
}) => {
  const baseRoute = `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/override_rules`;
  const levelsRoute = `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/levels`;

  const {
    isLoading: isRulesLoading,
    isError: isRulesError,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher<OverrideRuleWithOverridesInfo[]>(baseRoute);

  const {
    isLoading: isLevelsLoading,
    isError: isLevelsError,
    data: levels,
  } = useDataFetcher<AuthorityLevelInfo[]>(levelsRoute);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<OverrideRuleWithOverridesInfo | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  const handleAdd = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  const handleEdit = (rule: OverrideRuleWithOverridesInfo) => {
    setEditingRule(rule);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const runMutation = async (action: () => Promise<unknown>) => {
    setIsMutating(true);
    try {
      await action();
      await reloadRules();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsMutating(false);
    }
  };

  const handleDelete = (rule: OverrideRuleWithOverridesInfo) =>
    runMutation(() => axios.delete(`${baseRoute}/${rule.rule_id}`));

  const handleToggleEnabled = (rule: OverrideRuleWithOverridesInfo, nextEnabled: boolean) => {
    const payload: EnableOverrideRuleParams = { is_enabled: nextEnabled };
    return runMutation(() => axios.post(`${baseRoute}/${rule.rule_id}/enable`, payload));
  };

  const handleMove = (rule: OverrideRuleWithOverridesInfo, direction: 'up' | 'down') =>
    runMutation(() => axios.post(`${baseRoute}/${rule.rule_id}/move-${direction}`));

  const isLoading = isRulesLoading || isLevelsLoading;
  const isError = isRulesError || isLevelsError;

  return (
    <div className="mt-32">
      <div className="mb-12 flex items-center justify-between">
        <Heading variant={Heading.TYPES.H3}>{TEXTS.OVERRIDE_RULES_SECTION_TITLE}</Heading>
        <Button
          color="primary"
          onClick={handleAdd}
          startIcon={<PlusIcon iconColor="currentColor" size={12} />}
          disabled={!levels}
        >
          {TEXTS.ADD_OVERRIDE_RULE}
        </Button>
      </div>
      <AlertBanner
        alertType={AlertBanner.ALERT_TYPES.INFO}
        note={TEXTS.OVERRIDE_RULES_BANNER}
        withIcon
        className="mb-12"
      />
      {rules && (
        <OverrideRulesTable
          organization={organization}
          rules={rules}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleEnabled={handleToggleEnabled}
          onMoveUp={(rule) => handleMove(rule, 'up')}
          onMoveDown={(rule) => handleMove(rule, 'down')}
          disabled={isMutating}
        />
      )}
      {(isLoading || isError) && <SkeletonTable rowsCount={5} columnsCount={5} isError={isError} />}
      {dialogOpen && levels && (
        <UpsertOverrideRuleDialog
          organizationId={organizationId}
          arePaymentLimitsSplit={arePaymentLimitsSplit}
          levels={levels}
          existingRule={editingRule}
          onClose={handleDialogClose}
          onSaved={async () => {
            await reloadRules();
          }}
        />
      )}
    </div>
  );
};

export default OverrideRulesSection;
